import React, { useEffect, useRef, useState } from "react";
import "../../../../../css/RecruitmentScreen/Sections.css";
import FormField from "../FormField";
import YesNoRadio from "../RadioButton";
import UseDatepicker from "../Datepicker";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { writeDataS4 } from "../../../../../backend/Sections/Section4/writeDataS4";
import { readDataS4 } from "../../../../../backend/Sections/Section4/readDataS4";
import errorTrackerV2 from "../Validation/Utils/ErrorTrackerV2";
import axios from "axios";
import sicDescriptions from "../../Utils/data.json";
import {
  addSubmissionListener,
  getIsSubmitted,
} from "../../Utils/submissionListener";
import { auth } from "../../../../../backend/firebaseConfig";
import { ReactComponent as CloseIcon } from "../../../../../assets/icons/close.svg";
import { shouldBypassValidation } from "../../Utils/validationPolicy";

import { validateSection4 } from "../Validation/section4"; //  adjust path if needed
import InputField from "../../../../DesignSystem/Inputs/InputField";
import AlertBanner from "../../../../DesignSystem/AlertBanner/AlertBanner";
import PrimaryButton from "../../../../DesignSystem/Buttons/PrimaryButton";
import SearchField from "../../../../DesignSystem/Inputs/SearchField";
import DateField from "../../../../DesignSystem/Inputs/DateField";
import TextArea from "../../../../DesignSystem/Inputs/TextArea";

const Section4 = ({ onValidationStatusChange }) => {
  const sectionTopRef = useRef(null);
  const [formData, setFormData] = useState({
    heldDirectorships: null,
    futureIntentions: null,
    ifNoDetails: "",
    ifYesDetails: "",
    financialLiabilities: null,
    additionalInformation: "",
    directorshipDetails: [],
  });

  const [isSubmitted, setIsSubmitted] = useState(getIsSubmitted());
  const [showModal, setShowModal] = useState(false);
  const [officerData, setOfficerData] = useState([]);

  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");

  const [officers, setOfficers] = useState([]);
  const [companyDetails, setCompanyDetails] = useState({});
  const [companyNumber, setCompanyNumber] = useState("");

  // ---- submission listener
  useEffect(() => {
    const removeListener = addSubmissionListener(setIsSubmitted);
    setIsSubmitted(getIsSubmitted());
    return () => removeListener();
  }, [getIsSubmitted()]);

  // ---- load saved data
  useEffect(() => {
    const loadData = async () => {
      const data = await readDataS4();
      // console.log("Section 4: Data loaded from Firebase:", data);
      if (data) {
        // console.log(
        //   "Section 4: Setting form data, validation will trigger automatically",
        // );
        setFormData({
          additionalInformation: data.additionalInformation || "",
          heldDirectorships:
            data.heldDirectorships !== undefined
              ? data.heldDirectorships
              : null,
          futureIntentions:
            data.futureIntentions !== undefined ? data.futureIntentions : null,
          ifNoDetails: data.ifNoDetails || "",
          ifYesDetails: data.ifYesDetails || "",
          financialLiabilities:
            data.financialLiabilities !== undefined
              ? data.financialLiabilities
              : null,
          directorshipDetails: (data.directorshipDetails || []).map(
            (entry) => ({
              nameOfFirm: entry.nameOfFirm || "",
              natureOfBusiness: entry.natureOfBusiness || "",
              countryIncorporationStarted:
                entry.countryIncorporationStarted || "",
              timeSpent: entry.timeSpent || "",
              startDate: entry.startDate || "",
              endDate: entry.endDate || "",
              officerName: entry.officerName || "",
              officerRole: entry.officerRole || "",
            }),
          ),
        });
      }
    };

    loadData();
  }, []);

  // ---- validate (single source of truth)
  const runValidation = (nextData) => {
    const v = validateSection4(nextData);
    setErrors(v);
    setIsValid(v._isValid);

    // store in tracker for progress tabs
    errorTrackerV2.setSectionErrors("section4", v);
    onValidationStatusChange(v._isValid);

    return v._isValid;
  };

  useEffect(() => {
    runValidation(formData);
  }, [formData]);

  // ---- updates
  const handleChange = (e, index = null, field = null) => {
    const { name, value } = e.target;

    if (index !== null && field) {
      setFormData((prev) => {
        const updated = [...(prev.directorshipDetails || [])];
        updated[index] = { ...(updated[index] || {}), [field]: value };
        return { ...prev, directorshipDetails: updated };
      });
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addDirectorshipEntry = () => {
    setFormData((prev) => ({
      ...prev,
      directorshipDetails: [
        ...(prev.directorshipDetails || []),
        {
          nameOfFirm: "",
          natureOfBusiness: "",
          countryIncorporationStarted: "",
          timeSpent: "",
          startDate: "",
          endDate: "",
        },
      ],
    }));
  };

  const deleteDirectorshipEntry = () => {
    setFormData((prev) => {
      const list = [...(prev.directorshipDetails || [])];

      if (prev.heldDirectorships === false) {
        return { ...prev, directorshipDetails: list.slice(0, -1) };
      }

      if (list.length > 1) {
        return { ...prev, directorshipDetails: list.slice(0, -1) };
      }

      return prev;
    });
  };

  const handleAddOfficer = (officer) => {
    setFormData((prevFormData) => {
      const isDuplicate = (prevFormData.directorshipDetails || []).some(
        (existingEntry) =>
          existingEntry.nameOfFirm === officer.nameOfFirm &&
          existingEntry.startDate === officer.startDate,
      );

      if (isDuplicate) return prevFormData;

      return {
        ...prevFormData,
        directorshipDetails: [
          ...(prevFormData.directorshipDetails || []),
          {
            nameOfFirm: officer.nameOfFirm,
            officerName: officer.officerName,
            natureOfBusiness: officer.natureOfBusiness,
            countryIncorporationStarted: officer.countryIncorporationStarted,
            startDate: officer.startDate,
            endDate: officer.endDate,
            timeSpent: "",
          },
        ],
      };
    });

    setShowModal(false);
    setCompanyNumber("");
  };

  const OfficersModal = ({ show, onClose, officers }) => {
    if (!show) return null;

    return (
      <div className="officer-modal-overlay" onClick={onClose}>
        <div
          className="officer-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="officers-modal">
            <div className="officer-modal-header">
              <h2>Officer Details</h2>
              <CloseIcon onClick={onClose} />
            </div>

            {officers.length > 0 ? (
              <div className="complaint-table-container">
                <table className="complaint-table">
                  <thead>
                    <tr>
                      <th>Name of Firm</th>
                      <th>Officer's Name</th>
                      <th>
                        Country Incorporation
                        <br />
                        Started
                      </th>
                      <th>
                        Directorship Start <br /> and End Dates
                      </th>
                      <th>ADD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {officerData.map((directorship, index) => (
                      <React.Fragment key={index}>
                        <tr>
                          <td>
                            <InputField
                              className="input-cell"
                              name="nameOfFirm"
                              value={directorship.nameOfFirm}
                              readOnly={true}
                              onChange={(e) =>
                                handleChange(e, index, "nameOfFirm")
                              }
                              isTextArea
                              error={errors[`nameOfFirm_${index}`]}
                              multiline
                              rows={2}
                            />
                          </td>
                          <td>
                            <InputField
                              className="input-cell"
                              value={directorship.officerName}
                              placeholder="Time Spent"
                              onChange={(e) =>
                                handleChange(e, index, "timeSpent")
                              }
                              isTextArea
                              error={errors[`timeSpent_${index}`]}
                              multiline
                              rows={2}
                            />
                          </td>
                          <td>
                            <InputField
                              className="input-cell"
                              value={directorship.countryIncorporationStarted}
                              readOnly={true}
                              onChange={(e) =>
                                handleChange(
                                  e,
                                  index,
                                  "countryIncorporationStarted",
                                )
                              }
                              isTextArea
                              error={
                                errors[`countryIncorporationStarted_${index}`]
                              }
                              multiline
                              rows={2}
                            />
                          </td>
                          <td>
                            <div className="form-row input-group">
                              <UseDatepicker
                                name={`startDate_${index}`}
                                value={directorship.startDate}
                                dateFormat={"dd/MM/yyyy"}
                                readOnly={true}
                                err={errors[`startDate_${index}`]}
                                onChange={(date) =>
                                  handleChange(
                                    {
                                      target: {
                                        name: "startDate",
                                        value: date,
                                      },
                                    },
                                    index,
                                    "startDate",
                                  )
                                }
                              />
                              <UseDatepicker
                                name={`endDate_${index}`}
                                value={directorship.endDate}
                                readOnly={true}
                                dateFormat={"dd/MM/yyyy"}
                                err={errors[`endDate_${index}`]}
                                onChange={(date) =>
                                  handleChange(
                                    {
                                      target: { name: "endDate", value: date },
                                    },
                                    index,
                                    "endDate",
                                  )
                                }
                              />
                            </div>
                          </td>
                          <td
                            style={{
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <button
                              type="button"
                              className="add-button"
                              onClick={() => handleAddOfficer(directorship)}
                            >
                              <i className="fas fa-plus"></i>
                            </button>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="5">
                            <div className="separator" />
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No officers found.</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleSearchClick = async () => {
    if (!companyNumber) {
      setAlertMessage("Please enter a company number.");
      setAlertType("error");
      return;
    }

    try {
      const COMPANIES_HOUSE_API_KEY = "2e1d7357-3b60-4888-81b4-715ddc7c906b";

      const companyResponse = await axios.get(
        `https://api.company-information.service.gov.uk/company/${companyNumber}`,
        {
          auth: { username: COMPANIES_HOUSE_API_KEY, password: "" },
        },
      );

      const companyData = companyResponse.data;

      const sicCodes = companyData.sic_codes;
      const sicDescriptionsMapped =
        sicCodes && sicCodes.length > 0
          ? sicCodes.map((sicCode) => ({
              code: sicCode,
              description: sicDescriptions.sic_descriptions[sicCode] || sicCode,
            }))
          : [{ code: "N/A", description: "SIC codes not available" }];

      const mappedJurisdiction =
        sicDescriptions.jurisdiction[companyData.jurisdiction] ||
        `Unknown jurisdiction (${companyData.jurisdiction})`;

      setCompanyDetails(companyData);

      const officersResponse = await axios.get(
        `https://api.company-information.service.gov.uk/company/${companyNumber}/officers`,
        {
          auth: { username: COMPANIES_HOUSE_API_KEY, password: "" },
        },
      );

      const allOfficers = officersResponse.data.items || [];
      setOfficers(allOfficers);

      setOfficerData(
        allOfficers.map((officer) => ({
          nameOfFirm: companyData.company_name,
          natureOfBusiness: sicDescriptionsMapped
            .map((desc) => desc.description)
            .join(", "),
          countryIncorporationStarted: mappedJurisdiction,
          startDate: officer.appointed_on,
          endDate: officer.resigned_on
            ? officer.resigned_on
            : companyData.date_of_cessation || "",
          timeSpent: "",
          officerName: officer.name,
          officerRole: officer.officer_role,
        })),
      );

      setShowModal(true);
    } catch (e) {
      // console.error("Error fetching company data:", e);
      setAlertMessage(
        "Company not found or error fetching data. Please check the company number and try again.",
      );
      setAlertType("error");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowErrors(true);
    sectionTopRef.current?.scrollIntoView({ behavior: "smooth" });

    const ok = runValidation(formData);

    // Save data regardless of validation status
    writeDataS4(formData);

    // Show appropriate alert based on validation result
    if (ok || shouldBypassValidation(auth.currentUser?.uid)) {
      setAlertMessage("Data saved successfully!");
      setAlertType("success");
    } else {
      setAlertMessage(
        "Data saved with errors. Please review the fields marked with errors.",
      );
      setAlertType("alert");
    }
  };

  return (
    <form className="section-form" onSubmit={handleSubmit} ref={sectionTopRef}>
      {alertMessage && (
        <AlertBanner
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertMessage("")}
        />
      )}
      <h3>Section 4 – Company Directorship Details</h3>

      <div className="input-group-row">
        <div className="Content">
          Have you <strong>ever</strong> held any company Directorships or
          Partnerships whether current or not?
          <br />
          (Please include ALL Directorships or Partnerships ever held){" "}
          <span style={{ color: "var(--Error)" }}>*</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="heldDirectorships"
            value={formData.heldDirectorships}
            onChange={handleChange}
            error={showErrors && !!errors.heldDirectorships}
          />
        </div>
      </div>
      {formData.heldDirectorships === true && (
        <>
          <div className="input-group-row">
            <div className="Content">
              Company Number
              <span style={{ color: "var(--Error)" }}>*</span>
            </div>
            <SearchField
              name="companyNumber"
              value={companyNumber}
              placeholder="Company Number"
              error={errors.directorshipDetails}
              onChange={(e) => setCompanyNumber(e.target.value.trim())}
              OnSearch={handleSearchClick}
            />
            {/* check - 535515 */}
            {/* <button
              type="button"
              className="delete-button"
              onClick={handleSearchClick}
            >
              <i className="fas fa-search"></i>
            </button> */}
          </div>
          <div className="Content-Small">
            If multiple directorships are held at the same time, you must ensure
            that all relationships are disclosed. <br />
            You should also provide a breakdown of the time committed to each
            directorship.
          </div>

          <OfficersModal
            show={showModal}
            onClose={() => setShowModal(false)}
            officers={officerData}
          />

          {formData.directorshipDetails.length > 0 && (
            <>
              {" "}
              <div className="complaint-table-container">
                <table className="complaint-table">
                  <thead>
                    <tr>
                      <th>Name of Firm</th>
                      <th>Nature of Business</th>
                      <th>
                        Country Incorporation
                        <br />
                        Started
                      </th>
                      <th>
                        Directorship Start <br /> and End Dates
                      </th>
                      <th>
                        IF THIS DIRECTORSHIP
                        <br /> IS ACTIVE, PLEASE CONFIRM
                        <br /> HOW MANY HOURS PER <br />
                        MONTH YOU DEDICATE TO IT.
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(formData.directorshipDetails || []).map(
                      (directorship, index) => (
                        <tr key={index}>
                          <td>
                            <InputField
                              className="input-cell"
                              name="nameOfFirm"
                              value={directorship.nameOfFirm}
                              readOnly={true}
                              onChange={(e) =>
                                handleChange(e, index, "nameOfFirm")
                              }
                              isTextArea
                              error={errors[`nameOfFirm_${index}`]}
                            />
                          </td>
                          <td>
                            <InputField
                              className="input-cell"
                              value={directorship.natureOfBusiness}
                              onChange={(e) =>
                                handleChange(e, index, "natureOfBusiness")
                              }
                              isTextArea
                              readOnly={true}
                              longTextArea
                              multiline
                              error={errors[`natureOfBusiness_${index}`]}
                            />
                          </td>
                          <td>
                            <InputField
                              className="input-cell"
                              value={directorship.countryIncorporationStarted}
                              readOnly={true}
                              onChange={(e) =>
                                handleChange(
                                  e,
                                  index,
                                  "countryIncorporationStarted",
                                )
                              }
                              isTextArea
                              error={
                                errors[`countryIncorporationStarted_${index}`]
                              }
                            />
                          </td>
                          <td>
                            <div className="form-row input-group">
                              <DateField
                                name={`startDate_${index}`}
                                value={directorship.startDate}
                                dateFormat={"dd/MM/yyyy"}
                                readOnly={true}
                                err={errors[`startDate_${index}`]}
                                onChange={(date) =>
                                  handleChange(
                                    {
                                      target: {
                                        name: "startDate",
                                        value: date,
                                      },
                                    },
                                    index,
                                    "startDate",
                                  )
                                }
                              />
                              <DateField
                                name={`endDate_${index}`}
                                value={directorship.endDate}
                                readOnly={true}
                                dateFormat={"dd/MM/yyyy"}
                                err={errors[`endDate_${index}`]}
                                onChange={(date) =>
                                  handleChange(
                                    {
                                      target: { name: "endDate", value: date },
                                    },
                                    index,
                                    "endDate",
                                  )
                                }
                              />
                            </div>
                          </td>
                          <td>
                            <InputField
                              className="input-cell"
                              value={directorship.timeSpent}
                              placeholder="Time Spent"
                              onChange={(e) =>
                                handleChange(e, index, "timeSpent")
                              }
                              isTextArea
                              error={errors[`timeSpent_${index}`]}
                            />
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
              <div className="button-group">
                <button
                  type="button"
                  className="icon-button delete-button"
                  onClick={deleteDirectorshipEntry}
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
              <div className="input-group-row">
                <div className="Content">
                  Will the above active Directorship or Partnerships continue?
                  <span style={{ color: "var(--Error)" }}>*</span>
                </div>
                <div className="input-group">
                  <YesNoRadio
                    name="futureIntentions"
                    value={formData.futureIntentions}
                    onChange={handleChange}
                    error={showErrors && !!errors.futureIntentions}
                  />
                </div>
              </div>
              <div className="input-group-row">
                <div className="Content">
                  If no, what has happened/is happening to them?
                  {/* <span style={{ color: "var(--Error)" }}>*</span> */}
                </div>
                <InputField
                  name="ifNoDetails"
                  placeholder="Please provide details"
                  value={formData.ifNoDetails}
                  onChange={handleChange}
                  error={showErrors && !!errors.ifNoDetails}
                  helperText={showErrors ? errors.ifNoDetails : ""}
                  multiline
                  rows={4}
                />
              </div>
              <div className="input-group-row">
                <div className="Content">
                  If yes, please provide details of your future intentions and
                  the nature of your responsibilities.
                </div>
                <InputField
                  name="ifYesDetails"
                  isTextArea
                  placeholder="Please provide details"
                  value={formData.ifYesDetails}
                  onChange={handleChange}
                  error={showErrors && !!errors.ifYesDetails}
                  helperText={showErrors ? errors.ifYesDetails : ""}
                  multiline
                  rows={4}
                />
              </div>
            </>
          )}
        </>
      )}

      <div className="separator" />

      <div className="input-group-row">
        <div className="Content">
          Do you have any financial liabilities outstanding regarding this
          Directorship/Partnership or have you ever signed a Deed of Guarantee?
          <span style={{ color: "var(--Error)" }}>*</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="financialLiabilities"
            value={formData.financialLiabilities}
            onChange={handleChange}
            error={showErrors && !!errors.financialLiabilities}
          />
        </div>
      </div>
      {formData.financialLiabilities === true && (
        <TextArea
          multiline
          rows={4}
          placeholder="Enter your text here..."
          name="additionalInformation"
          value={formData.additionalInformation}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              additionalInformation: e.target.value,
            }))
          }
          error={
            formData.financialLiabilities === true &&
            (formData.additionalInformation || "").trim() === ""
              ? "This field is required"
              : false
          }
          helperText={
            showErrors &&
            formData.financialLiabilities === true &&
            (formData.additionalInformation || "").trim() === ""
              ? "This field is required"
              : ""
          }
          showError={showErrors}
        />
      )}

      {!isSubmitted && <PrimaryButton label="SAVE" onClick={handleSubmit} />}
    </form>
  );
};

export default Section4;
